import { ReactNode } from 'react'
import { usePricingEditorRoot } from './usePricingEditor.root'
import { PricingEditorProvider } from '../communication'
import { EditorMode, PricingEditorPortErrors, Product } from '../domain'
import { ListPriceLayout } from './layout/listPrice/ListPriceLayout'
import { VariantPriceLayout } from './layout/variantPrice/VariantPriceLayout'
import { Button, Modal, Flex } from '@sequencehq/core-components'
import { Outlet } from 'react-router-dom'
import NotFound from 'components/ErrorPages/NotFound'
import PageError from 'components/ErrorPages/PageError'
import { Currency } from '@sequencehq/api'
import { match } from 'ts-pattern'
import { VariantPrice } from '../communication/external/v1/ports'

const ErrorModal = (props: {
  error: PricingEditorPortErrors
  onCancel?: () => void
}) => {
  return (
    <Modal
      isOpen
      variant="drawer"
      onClose={() => props.onCancel?.()}
      data-testid="priceEditor.error"
    >
      <Modal.Header showCloseButton withBorder>
        Error opening pricing editor
      </Modal.Header>
      <Modal.Content padding={0}>
        {props.error === PricingEditorPortErrors.NotFound ? (
          <NotFound />
        ) : (
          <PageError />
        )}
      </Modal.Content>
      <Modal.Footer withBorder>
        <Flex justifyContent="flex-end" width="100%">
          <Button
            onClick={props.onCancel}
            variant="secondary"
            data-testid="priceEditor.error.cancel"
          >
            Close
          </Button>
        </Flex>
      </Modal.Footer>
    </Modal>
  )
}

type CommonProps = {
  initialMode: EditorMode
  currency?: Currency
  variantPriceId?: string
  onSave?: (data: { price: any }) => void
  onCancel?: () => void
  duplicateFrom?: string
}

/**
 * PricingEditorRoot
 * @param existingPrice - (Variant only) the existing price to load into the editor. Must be
 * provided when the defaultPricePorts are disabled and editing a price.
 * @param disableDefaultPricePorts - (Variant only) disable default loading and saving for the price entity.
 * The calling component/module will need to load and save the price entity itself. Supporting
 * data for the price editor, such as list prices, will still function.
 * @returns
 */
export const PricingEditorRoot = (
  props:
    | ({
        variant: 'variant'
        existingPrice?: VariantPrice
        existingProduct?: Product
        disableDefaultPricePorts?: boolean
        disableDefaultProductPorts?: boolean
        drawerLeadingContent?: ReactNode
      } & CommonProps)
    | ({ variant: 'list' } & CommonProps)
) => {
  const { domain, ready, error } = usePricingEditorRoot({
    variant: props.variant,
    initialMode: props.initialMode,
    currency: props.currency,
    variantPriceId: props.variantPriceId,
    onSave: props.onSave,
    existingPrice:
      props.variant === 'variant' ? props.existingPrice : undefined,
    existingProduct:
      props.variant === 'variant' ? props.existingProduct : undefined,
    disableDefaultPricePorts:
      props.variant === 'variant' ? props.disableDefaultPricePorts : undefined,
    disableDefaultProductPorts:
      props.variant === 'variant'
        ? props.disableDefaultProductPorts
        : undefined,
    duplicateFrom: props.duplicateFrom
  })

  if (error) {
    return <ErrorModal error={error} onCancel={props.onCancel} />
  }

  return (
    <PricingEditorProvider value={domain}>
      {match(props)
        .with({ variant: 'list' }, () => (
          <ListPriceLayout
            loading={!ready}
            initialMode={props.initialMode}
            onCancel={props.onCancel}
          />
        ))
        .with({ variant: 'variant' }, variantProps => (
          <VariantPriceLayout
            onCancel={props.onCancel}
            loading={!ready}
            variantPriceId={props.variantPriceId}
            drawerLeadingContent={variantProps.drawerLeadingContent}
          />
        ))
        .exhaustive()}
      <Outlet />
    </PricingEditorProvider>
  )
}
